
import React from "react";
import { useTranslation } from "react-i18next";
import {
  House, UserCircle, BookOpen, ChartLine, Users, GraduationCap, Receipt,
  ChartLineUp, Megaphone, SignOut, CalendarCheck, Calendar as CalendarIcon,
  Gift, ClockAfternoon, UsersThree, ClockCounterClockwise, Clock,
  CheckCircle, Folder, CurrencyDollar, Heart, Smiley, UserGear, Briefcase,
  Gear
} from "@phosphor-icons/react";

import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarRail,
  useSidebar
} from "@/components/ui/sidebar";
import { Button } from "@/components/ui/button";
import { useApp } from "@/context/AppContext";

export function AppSidebar() {
  const { t } = useTranslation();
  const { 
    currentUser, 
    activeSection, 
    setActiveSection 
  } = useApp();
  
  const { isMobile, setOpenMobile } = useSidebar();

  const handleItemClick = (id: string) => {
    setActiveSection(id);
    if (isMobile) {
      setOpenMobile(false);
    }
  };

  const navItems = [
    { id: "dashboard", label: t("dashboard"), icon: House },
    { id: "core-hr", label: "Core HR & PIM", icon: UserCircle },
    { id: "lms", label: "Learning (LMS)", icon: BookOpen },
    { id: "performance", label: "Performance", icon: ChartLine },
    { id: "recruitment", label: "Recruitment & ATS", icon: Users },
    { id: "lifecycle", label: "Onboarding & Lifecycle", icon: GraduationCap },
    { id: "payroll", label: "Payroll & Expenses", icon: Receipt },
    { id: "analytics", label: "AI & Analytics", icon: ChartLineUp },
    { id: "announcements", label: "Announcements", icon: Megaphone },
    
    // Onboarding for HR, admins and superadmins only
    ...(currentUser?.role === "hr" ||
    currentUser?.role === "admin" ||
    currentUser?.role === "superadmin"
      ? [{ id: "onboarding", label: "Onboarding", icon: GraduationCap }]
      : []),
      
    // Offboarding for HR, admins and superadmins only
    ...(currentUser?.role === "hr" ||
    currentUser?.role === "admin" ||
    currentUser?.role === "superadmin"
      ? [{ id: "offboarding", label: "Offboarding", icon: SignOut }]
      : []),
      
    { id: "attendance", label: t("attendance"), icon: CalendarCheck },
    { id: "leaves", label: t("leave"), icon: CalendarIcon },
    { id: "holidays", label: "Holidays", icon: Gift },
    
    // Shift Management
    ...(currentUser?.role === "manager" ||
    currentUser?.role === "hr" ||
    currentUser?.role === "admin" ||
    currentUser?.role === "superadmin"
      ? [{ id: "shifts", label: "Shift Schedules", icon: ClockAfternoon }]
      : []),
      
    // Team Calendar
    ...(currentUser?.role === "manager" ||
    currentUser?.role === "hr" ||
    currentUser?.role === "admin" ||
    currentUser?.role === "superadmin"
      ? [{ id: "team-calendar", label: "Team Calendar", icon: UsersThree }]
      : []),
      
    // Team Attendance
    ...(currentUser?.role === "manager" ||
    currentUser?.role === "hr" ||
    currentUser?.role === "admin" ||
    currentUser?.role === "superadmin"
      ? [{ id: "team-attendance", label: "Team Attendance", icon: ClockCounterClockwise }]
      : []),
      
    { id: "timesheet", label: t("timesheet"), icon: Clock },
    
    // Approvals
    ...(currentUser?.role === "manager" ||
    currentUser?.role === "hr" ||
    currentUser?.role === "admin" ||
    currentUser?.role === "superadmin"
      ? [{ id: "approvals", label: "Approvals", icon: CheckCircle }]
      : []),
      
    { id: "employees", label: "Team", icon: Users },
    { id: "org-chart", label: "Org Chart", icon: UsersThree },
    { id: "documents", label: "Documents", icon: Folder },
    { id: "expenses", label: "Expenses", icon: CurrencyDollar },
    { id: "wfh", label: "Work From Home", icon: House },
    
    // Engagement
    { id: "recognition", label: "Recognition", icon: Heart },
    { id: "rewards", label: "Rewards", icon: Gift },
    { id: "surveys", label: "Surveys", icon: Smiley },
    { id: "courses", label: "Learning", icon: BookOpen },
    { id: "profile", label: "My Profile", icon: UserCircle },
    { id: "reports", label: "Reports", icon: ChartLine },
    
    // User Management (Admin/HR/Superadmin)
    ...(currentUser?.role === "admin" ||
    currentUser?.role === "superadmin" ||
    currentUser?.role === "hr"
      ? [{ id: "users", label: "User Management", icon: UserGear }]
      : []),
      
    // Organization (Admin/Superadmin)
    ...(currentUser?.role === "admin" ||
    currentUser?.role === "superadmin"
      ? [{ id: "organization", label: "Organization", icon: Briefcase }]
      : []),
  ];

  return (
    <Sidebar collapsible="icon">
      <SidebarHeader className="border-b border-border p-4">
        <div className="flex items-center gap-2 px-2">
          <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-primary text-primary-foreground">
            <span className="text-lg font-bold">F</span>
          </div>
          <div className="flex flex-col gap-0.5 leading-none group-data-[collapsible=icon]:hidden">
            <span className="font-semibold">FlowHR</span>
            <span className="text-xs text-muted-foreground">HR Management</span>
          </div>
        </div>
      </SidebarHeader>
      
      <SidebarContent>
        <SidebarGroup>
          <SidebarGroupLabel>Menu</SidebarGroupLabel>
          <SidebarGroupContent>
            <SidebarMenu>
              {navItems.map((item) => {
                const Icon = item.icon;
                const isActive = activeSection === item.id;
                
                return (
                  <SidebarMenuItem key={item.id}>
                    <SidebarMenuButton
                      isActive={isActive}
                      tooltip={item.label}
                      onClick={() => handleItemClick(item.id)}
                      className={isActive ? "bg-primary text-primary-foreground hover:bg-primary/90 hover:text-primary-foreground" : ""}
                    >
                      <Icon className={isActive ? "text-primary-foreground" : ""} />
                      <span>{item.label}</span>
                    </SidebarMenuButton>
                  </SidebarMenuItem>
                );
              })}
            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>
      </SidebarContent>
      
      <SidebarFooter className="border-t border-border p-2">
        <SidebarMenu>
          <SidebarMenuItem>
            <SidebarMenuButton
              isActive={activeSection === "settings"}
              tooltip="Settings"
              onClick={() => handleItemClick("settings")}
              className={activeSection === "settings" ? "bg-primary text-primary-foreground hover:bg-primary/90 hover:text-primary-foreground" : ""}
            >
              <Gear className={activeSection === "settings" ? "text-primary-foreground" : ""} />
              <span>Settings</span>
            </SidebarMenuButton>
          </SidebarMenuItem>
        </SidebarMenu>
      </SidebarFooter>
      <SidebarRail />
    </Sidebar>
  );
}
